import React, { useEffect, useRef } from "react";
import "hammerjs";
import { Outlet } from "react-router";
import {
  ID_TOKEN_LOCAL_STORAGE_KEY,
  REFRESH_TOKEN_LOCAL_STORAGE_KEY,
  TOKEN_EXPIRY_TIME_LOCAL_STORAGE_KEY,
} from "@/configs/site";
import { useUserState } from "@/context/UserContext";
import "@/libs/axios";
import "@/libs/i18n";
import { clearAuthStorage, isTokenExpired } from "@/libs/store";
import useLogin from "@/pages/login/useLogin";
import "./App.scss";

/**
 * Root App component that handles initialization logic
 * This component is always rendered and handles auto-login
 */
const App = () => {
  const { isAuthenticated } = useUserState();
  const { onLogin } = useLogin();
  // Auto-login must fire at most once per token. `onLogin` sets loading/error
  // state synchronously, which re-renders App before `isAuthenticated` flips,
  // so the guard below cannot rely on `isAuthenticated` alone.
  const attemptedToken = useRef<string | null>(null);

  useEffect(() => {
    if (isAuthenticated) return;

    const idToken = localStorage.getItem(ID_TOKEN_LOCAL_STORAGE_KEY) || "";
    if (!idToken.length || attemptedToken.current === idToken) return;

    if (isTokenExpired()) {
      // Route loaders do run first (`publicRouteLoader` / `rootRouteLoader`),
      // but `hasValidSession()` returns early on `!user.userId` *before* its
      // expiry branch — so in the post-SSO state this effect exists for (token
      // stored, no `user` yet) an expired token survives the loader and lands
      // here. Without this guard it is auto-logged-in (401 -> logOut() -> full
      // page reload) and attached to the subsequent /api/login request.
      // Deliberately no `setSessionExpired()`: the login page reads that flag
      // during render, so setting it now would only surface a stale notice on
      // a later navigation.
      clearAuthStorage();
      return;
    }

    attemptedToken.current = idToken;
    onLogin(
      { loginId: idToken },
      idToken,
      localStorage.getItem(TOKEN_EXPIRY_TIME_LOCAL_STORAGE_KEY) || "",
      localStorage.getItem(REFRESH_TOKEN_LOCAL_STORAGE_KEY) || "",
    );
  }, [isAuthenticated, onLogin]);

  // App component handles initialization, routing is handled by RouterProvider
  return <Outlet />;
};

export default App;
